/**
 * Rosalie's kinematic movement model. These tuning values are intentionally
 * un-tuned first guesses until the M0a feel pass can judge them in the test room.
 */

import type { PhysicsWorld } from '@/physics/world';

export type Gait = 'still' | 'creep' | 'walk' | 'trot' | 'roll' | 'scramble';

/** Every number a designer would tune. One object, one place. */
export interface CharacterTuning {
  walkSpeed: number; trotSpeed: number; creepSpeed: number;
  rollMaxSpeed: number; rollMinEntrySpeed: number;
  accel: number; decel: number; rollAccel: number; rollDrag: number;
  airAccel: number;
  turnRateWalk: number; turnRateTrot: number; turnRateRoll: number;
  gravity: number; maxFallSpeed: number;
  coyoteTime: number; inputBufferTime: number;
  autostepMaxHeight: number; autostepMinWidth: number;
  scrambleMinHeight: number; scrambleMaxHeight: number;
  scrambleDuration: number; scrambleForwardClearance: number; scrambleCooldown: number;
  /** Seconds of pressing into a ledge before she commits to hauling up it. */
  scramblePressTime: number;
  maxSlopeClimbAngle: number; minSlopeSlideAngle: number;
  snapToGroundDistance: number;
  slopeRollAccelScale: number;
  /** Seconds of continuous rolling before fatigue drag starts building. */
  rollFatigueDelay: number;
  /** Seconds from the delay to full fatigue. */
  rollFatigueRamp: number;
  /** Extra drag at full fatigue, added to `rollDrag`. */
  rollFatigueDrag: number;
  /** Seconds of not rolling before she is fully recovered. */
  rollRecoverTime: number;
  /**
   * Seconds a roll runs before releasing the key can end it.
   *
   * The roll is a committed action, not a held state. Below this, letting go
   * does nothing and she keeps tumbling.
   */
  rollCommitTime: number;
  /** Seconds after a roll ends before another can start. */
  rollCooldown: number;
  radius: number; halfHeight: number;
  /**
   * Total collider height while creeping, metres. Must exceed 2*radius, which
   * is the shortest a capsule of that radius can be.
   */
  crouchHeight: number;
}

export const ROSALIE_TUNING: CharacterTuning = {
  // WHY: 1.1 m/s reads as a deliberate, small animal's default pace.
  walkSpeed: 1.1,
  // WHY: 1.9 m/s gives an eager trot without making the grey-box room tiny.
  trotSpeed: 1.9,
  // WHY: 0.65 m/s keeps stealth movement visibly and audibly restrained.
  creepSpeed: 0.65,
  // WHY: 4.5 m/s makes the roll exhilarating while still controllable at 60 Hz.
  rollMaxSpeed: 4.5,
  // WHY: requiring a trot preserves roll as momentum, not an instant sprint button.
  rollMinEntrySpeed: 1.35,
  // WHY: quick acceleration makes keyboard input responsive without snapping.
  accel: 7,
  // WHY: stronger braking prevents the unwanted ice-skating the brief rejects.
  decel: 11,
  // WHY: rolling needs a sustained shove to build its own playful momentum.
  rollAccel: 4.2,
  // WHY: drag makes releasing a roll settle predictably instead of coasting forever.
  rollDrag: 1.35,
  // WHY: limited air steering preserves the weight of a launched roll.
  airAccel: 2.2,
  // WHY: walking can face a new command promptly without looking robotic.
  turnRateWalk: 9,
  // WHY: SLOWER than walking, not faster. Momentum is the point — a character
  // who pivots harder the faster she goes reads as weightless, and weight is
  // the entire M0a gate.
  turnRateTrot: 6.5,
  // WHY: the lower roll rate protects its committed, momentum-led feeling.
  turnRateRoll: 3.2,
  // WHY: this is earth gravity in metres, matching the physics world's default.
  gravity: 9.81,
  // WHY: terminal speed is capped so long falls remain stable and readable.
  maxFallSpeed: 12,
  // WHY: a short grace period forgives stepping over tiny ground gaps.
  coyoteTime: 0.1,
  // WHY: a short buffer makes a near-landing roll command feel responsive.
  inputBufferTime: 0.12,
  // WHY 0.26 and not 0.20: **Rapier's autostep height is not the step height it
  // will actually climb.** Measured on the test room's ladder, walking at each
  // rung rather than spawning on it:
  //
  //   nominal 0.20 → the 0.16 m step is REFUSED (she wedges at y = 0.126)
  //   nominal 0.26 → the 0.16 m step is climbed cleanly
  //   nominal 0.34 → no further change
  //
  // The effective threshold is roughly 0.6x the nominal value, presumably
  // because the controller's skin offset and the shape cast both eat into it.
  // Whatever the mechanism, the number here is a Rapier parameter and NOT a
  // height in metres, which is the trap: 0.20 reads as "she climbs 20 cm" and
  // meant "she climbs about 12".
  //
  // The cost of getting it wrong was the whole middle of D-012's ladder. It read
  // trivial (0.08) → BLOCKED (0.16) → scramble (0.32) → refused (0.48), and 0.16
  // is the EVERYDAY step-up. Alex reported it as autostep "mostly did nothing",
  // and no test caught it because the ladder was only ever checked by spawning
  // her on each step and confirming she stayed there.
  //
  // Constrained from above by the scramble: `scrambleMinHeight` (0.22) is a real
  // measured obstacle height, so the two are in different units and cannot be
  // compared directly. What matters is the observed behaviour, and it is
  // asserted — the 0.32 m rung still requires a scramble and the 0.48 m rung is
  // still refused. If you change this, re-run that whole test, not just this rung.
  autostepMaxHeight: 0.26,
  // WHY: narrower than her body. Rapier requires this much clear floor at the
  // top of a step before it will take it; at her footprint width it was
  // rejecting legitimate steps whose landing was fine.
  autostepMinWidth: 0.12,
  // WHY: 0.22 m is an un-tuned first guess just above the 0.20 m silent
  // autostep, so a scramble stays a distinct effort rather than a redundant one.
  scrambleMinHeight: 0.22,
  // WHY: 0.34 m is an un-tuned first guess just above Rosalie's 0.32 m height.
  // Raising this past about 0.40 m would break D-012's built-for-boots thesis
  // and must not be done to make the room easier.
  scrambleMaxHeight: 0.34,
  // WHY: 0.72 s is an un-tuned first guess long enough to read as a heave, with
  // a slow start and settled finish rather than a disguised instant step.
  scrambleDuration: 0.72,
  // WHY: 0.35 m is an un-tuned first guess that lands her safely beyond a ledge
  // face while remaining well inside the test room step's 2.4 m top.
  scrambleForwardClearance: 0.35,
  // WHY: 0.3 s is an un-tuned first guess that prevents a held request from
  // immediately chaining another scramble after she reaches the top.
  scrambleCooldown: 0.3,
  // WHY: 0.18 s of leaning into it. Long enough that clipping a wall corner in
  // passing never triggers a climb, short enough that a deliberate approach
  // feels immediate rather than delayed. This is the whole input for the verb —
  // there is no scramble key, by D-017.
  scramblePressTime: 0.18,
  // WHY: 38 degrees is a generous playable incline but not a wall-climb.
  maxSlopeClimbAngle: 0.66,
  // WHY: steeper surfaces slide, making the ramp and hills legible rather than sticky.
  minSlopeSlideAngle: 0.8,
  // WHY: a small snap closes fixed-step floor seams without visibly pulling her down.
  snapToGroundDistance: 0.06,
  // WHY: grade contribution rewards a downhill roll without overpowering player input.
  slopeRollAccelScale: 6.5,
  // WHY: 1.5 s of free rolling. Counted in REVOLUTIONS rather than seconds,
  // which is how a player experiences it: at 4.5 m/s over her 1.39 m perimeter
  // that is about five turns before she starts to tire and roughly eleven
  // before she is done. Two earlier values (3.5 s, then 2.5 s) both came back
  // as "quite a lot of rotations" — seconds are the wrong unit to tune this in.
  rollFatigueDelay: 1.5,
  // WHY: drag arrives over 2 s rather than at a threshold. She should feel
  // herself running out, not hit a wall — a hard cutoff mid-roll reads as the
  // game taking control away, which is the opposite of §5.4's "joyful".
  rollFatigueRamp: 2,
  // WHY: enough to overhaul roll acceleration on the flat, so she coasts down
  // and must re-enter. NOT enough to stop her downhill: a hill still wins,
  // which keeps the authored roll runs intact.
  rollFatigueDrag: 4.5,
  // WHY: she recovers faster than she tires, so chaining rolls stays viable and
  // this never becomes a resource to manage. There is no meter and there will
  // not be one — spec §9 wants near-zero HUD, and D-009 already replaced the
  // detection meter with breathing for exactly this reason.
  rollRecoverTime: 2,
  // WHY: about one and a half revolutions at trot entry speed — long enough that
  // a roll always reads as a whole deliberate movement rather than a twitch, and
  // short enough that it never feels like the game took the controls away. Sits
  // comfortably under rollFatigueDelay so committing never costs stamina.
  rollCommitTime: 0.85,
  // WHY: she has to gather herself before throwing her body again. Short enough
  // that chaining rolls down the escape chute (§5.4) still flows — D-017 makes
  // recovery faster than fatigue on purpose and this must not quietly undo that.
  rollCooldown: 0.4,
  // WHY: 0.125 m leaves a forgiving inset inside Rosalie's 0.30 m visual width.
  // Tracks ROSALIE_SCALE.width — it narrowed at G1 and this narrowed with it, or
  // she would collide with doorways her body visibly fits through.
  radius: 0.125,
  // WHY: capsule height is 2*(halfHeight + radius), so 0.035 + 0.125 gives the
  // 0.32 m that ROSALIE_SCALE.height locks. This was left at 0.02 when the
  // radius narrowed at G1, quietly making her collider 0.29 m — 3 cm shorter
  // than she looks, which is a gap she would have squeezed under.
  halfHeight: 0.035,
  // WHY: 0.26 m against 0.32 m standing. Bounded from below by the capsule
  // itself — 2*radius is 0.25 m and a capsule cannot be shorter — so this is
  // very nearly as low as she can go without narrowing her as well. The test
  // room's tunnel is sized to it, at 0.28 m of clearance.
  //
  // Until this existed, crouching was COSMETIC: the clip lowered her and the
  // collider did not, so she could not fit under anything and the entire verb
  // was "press Ctrl and watch her get shorter".
  crouchHeight: 0.26,
};

/** What the controller is told each fixed step. Camera-relative, already resolved. */
export interface MoveCommand {
  /** World-space desired direction, XZ only, length 0..1. Zero = no input. */
  moveX: number; moveZ: number;
  crouch: boolean;
  roll: boolean;
  /**
   * A facing to turn toward IN PLACE, at the walk turn rate, when there is no
   * movement input — D-209's wayfinding turn. Ignored the moment the player
   * steers, so it can never fight a held key. Absent: she keeps her facing.
   */
  face?: number;
}

/** What the animation, camera and audio systems read. Pure data, no THREE types. */
export interface CharacterState {
  x: number; y: number; z: number;
  facing: number;
  speed: number;
  velocityY: number;
  gait: Gait;
  grounded: boolean;
  airborneTime: number;
  landingImpact: number;
  turnRate: number;
  slopeGrade: number;
  /** 0 when not scrambling, otherwise eased-action progress from 0 to 1. */
  scramble: number;
}

/**
 * Where she starts, and **which way she is pointing when she gets there**.
 *
 * The facing is optional and defaults to 0, which is world +Z, because that is
 * what every caller got before this field existed and a spawn that silently
 * rotated would be a change to the feel of every respawn in the suite.
 *
 * It exists because beat 1's opening shot is authored *at her face*, and until
 * this field she woke facing +Z — across a creep strip that is 0.54 m deep and
 * 2.1 m long. An animal lying in that strip lies along it. The authored camera
 * had therefore always stood off her flank, which is why the opening frame read
 * as a pale mass rather than as a piglet (R-024, D-058). No camera offset can
 * find a face that is turned ninety degrees away from it, so the fix is here
 * rather than in the framing.
 */
export interface SpawnPoint {
  x: number;
  y: number;
  z: number;
  /** World radians; forward is (sin f, cos f). Omit for +Z. */
  facing?: number;
}

/**
 * Where a shot puts her body, and how fast the host should say she is going.
 *
 * `speed` exists for beat 14's four steps: `character.reset` zeroes her
 * velocity, so a carried body used to animate as standing still however far
 * the shot moved it — beat 13's surge slides her along the fence in an idle.
 * Non-zero `speed` makes the host report a walk to the animation (legs and
 * footfalls) for that tick; the controller itself stays reset.
 */
export interface CarriedPose extends SpawnPoint {
  readonly speed?: number;
}

type GroundNormal = { x: number; y: number; z: number };

const UP: GroundNormal = { x: 0, y: 1, z: 0 };
// WHY: Rapier requires a non-zero skin gap for stable character collision queries.
const CHARACTER_CONTROLLER_OFFSET = 0.01;

/**
 * Downward speed used to seat her on the floor while grounded, m/s.
 *
 * **This number was wrong twice, and the second time the number was never the
 * problem.** At 1.5 m/s it closed the hover gap beautifully and quietly broke
 * every slope in the game: the downward component of the REQUESTED MOVEMENT was
 * larger than the forward one, so walking into an incline resolved as "blocked"
 * and the controller slid her sideways along the ramp instead of up it.
 *
 * 0.25 m/s looked like the fix and was the same bug one scale down: it did not
 * break the ramp, it deleted every step in the game. Measured by walking her at
 * each rung of the test room's ladder **from open floor**, which no test had
 * ever done — the two walk-onto tests both spawned her INSIDE a taller rung and
 * recorded her walking DOWN off it (D-146, and D-100 exactly):
 *
 *   stick 0.25, in the solver request → 0.08 REFUSED, 0.09 REFUSED, 0.16 REFUSED
 *   stick 0.01, in the solver request → 0.08 climbed, 0.09 climbed, 0.16 REFUSED
 *   stick 0                           → 0.08, 0.09 and 0.16 climbed, and she
 *                                       hovers up to 50 mm off the floor instead
 *
 * So the stick was never competing with the slope or with the step height. It
 * was competing with the step-up itself, and any value large enough to seat her
 * in a reasonable number of frames is large enough to take the whole ladder with
 * it. **The fix is where it is applied, not how big it is:** the solver is asked
 * for a purely horizontal move and the seating happens AFTER it, in
 * `#seatOnGround()`. 0.25 is therefore back to its own stated reasoning — it
 * closes a 30 mm gap in about seven frames, fast enough that nobody sees it.
 *
 * **What this does NOT fix, and do not read it as fixed:** with the seating
 * moved out of the way she climbs 0.08…0.12 and is refused by 0.13 and above.
 * That ceiling is her capsule radius (0.1275 m) and it is not Rapier's autostep
 * — `autostepMaxHeight` does not move it, and raising it to 1.0 makes her worse
 * rather than higher. What is actually happening is her own rounded capsule
 * bottom riding over the edge across several frames. D-012's 0.16 m "everyday
 * step-up" therefore does not work and never has. See D-148.
 */
const GROUND_STICK_SPEED = 0.25;

/** Straight down, in the unit length `castShape` reports its impact in. */
const DOWN = { x: 0, y: -1, z: 0 } as const;
/** No rotation: her capsule is upright in world space and never tips. */
const UPRIGHT = { x: 0, y: 0, z: 0, w: 1 } as const;

export class CharacterController {
  readonly #world: PhysicsWorld;
  readonly #tuning: CharacterTuning;
  readonly #body;
  readonly #collider;
  readonly #controller;
  #velocityX = 0;
  #velocityY = 0;
  #velocityZ = 0;
  #grounded = false;
  #groundNormal: GroundNormal = UP;
  #airborneTime = 0;
  #coyoteRemaining = 0;
  #rollBufferRemaining = 0;
  /** How long she has been pressing forward into a blocking ledge, seconds. */
  #scramblePressTime = 0;
  /** True while the collider is at its crouched height. */
  #crouched = false;
  #previousRollHeld = false;
  #rolling = false;
  /** Seconds spent continuously rolling. Drives fatigue drag; decays when not rolling. */
  #rollHeldTime = 0;
  /** Seconds left before another roll may be entered. See rollCooldown. */
  #rollCooldownRemaining = 0;
  /**
   * Seconds since THIS roll began. Separate from `#rollHeldTime` on purpose.
   *
   * The commit window originally read `#rollHeldTime`, which is the fatigue
   * accumulator — it decays between rolls rather than resetting, so a long roll
   * left it above `rollCommitTime` and the NEXT roll could be cancelled on its
   * first frame. The commit would silently stop working exactly for the player
   * who had been rolling the most. Caught on independent review.
   */
  #rollElapsed = 0;
  #scrambling = false;
  #scrambleElapsed = 0;
  #scrambleCooldownRemaining = 0;
  #scrambleStartX = 0;
  #scrambleStartY = 0;
  #scrambleStartZ = 0;
  #scrambleTargetX = 0;
  #scrambleTargetY = 0;
  #scrambleTargetZ = 0;
  #scramblePreviousX = 0;
  #scramblePreviousY = 0;
  #scramblePreviousZ = 0;
  #scrambleFacing = 0;
  #scrambleRay: InstanceType<PhysicsWorld['rapier']['Ray']>;
  /** Separate from the scramble ray: both can be live within one step. */
  #headroomRay: InstanceType<PhysicsWorld['rapier']['Ray']>;
  #scrambleNextCenter = { x: 0, y: 0, z: 0 };
  #disposed = false;
  #state: CharacterState;

  constructor(world: PhysicsWorld, tuning: CharacterTuning, spawn: SpawnPoint) {
    this.#world = world;
    this.#tuning = tuning;
    const centerY = spawn.y + tuning.halfHeight + tuning.radius + CHARACTER_CONTROLLER_OFFSET;
    this.#body = world.world.createRigidBody(
      world.rapier.RigidBodyDesc.kinematicPositionBased().setTranslation(spawn.x, centerY, spawn.z),
    );
    this.#collider = world.world.createCollider(
      world.rapier.ColliderDesc.capsule(tuning.halfHeight, tuning.radius), this.#body,
    );
    this.#controller = world.world.createCharacterController(CHARACTER_CONTROLLER_OFFSET);
    this.#controller.setApplyImpulsesToDynamicBodies(false);
    this.#controller.enableAutostep(tuning.autostepMaxHeight, tuning.autostepMinWidth, false);
    this.#controller.setMaxSlopeClimbAngle(tuning.maxSlopeClimbAngle);
    this.#controller.setMinSlopeSlideAngle(tuning.minSlopeSlideAngle);
    this.#controller.enableSnapToGround(tuning.snapToGroundDistance);
    this.#scrambleRay = new world.rapier.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: -1, z: 0 });
    this.#headroomRay = new world.rapier.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: 1, z: 0 });
    this.#state = this.#makeState(spawn.x, spawn.y, spawn.z, spawn.facing ?? 0, 0, 'still', false, 0, 0, 0, 0, 0);
  }

  update(dt: number, cmd: MoveCommand): CharacterState {
    if (this.#disposed) throw new Error('CharacterController has been disposed');

    const wasGrounded = this.#grounded;
    const preResolutionVelocityY = this.#velocityY;
    const input = normaliseInput(cmd.moveX, cmd.moveZ);
    // MoveCommand has only a held level. Remembering its prior value infers a
    // rising edge so a just-released request can survive the short designer-tuned
    // buffer window; once rolling, release still ends the hold-to-roll action.
    const rollRising = cmd.roll && !this.#previousRollHeld;
    if (rollRising) this.#rollBufferRemaining = this.#tuning.inputBufferTime;

    // A scramble is committed motion: while it is active, no new movement,
    // gravity or roll-entry logic gets a chance to change its trajectory.
    if (this.#scrambling) {
      this.#previousRollHeld = cmd.roll;
    this.#updateCrouchCollider(cmd.crouch);
      this.#rollBufferRemaining = 0;
      return this.#advanceScramble(dt);
    }

    if (wasGrounded) this.#coyoteRemaining = this.#tuning.coyoteTime;
    else this.#coyoteRemaining = Math.max(0, this.#coyoteRemaining - dt);

    const currentSpeed = Math.hypot(this.#velocityX, this.#velocityZ);
    const rollRequested = cmd.roll || this.#rollBufferRemaining > 0;
    const canEnterRoll = rollRequested && (wasGrounded || this.#coyoteRemaining > 0)
      && currentSpeed >= this.#tuning.rollMinEntrySpeed
      && this.#rollCooldownRemaining <= 0;
    // A roll is COMMITTED motion, like the scramble. Releasing the key inside the
    // commit window asks her to stop rolling; it does not stop her mid-tumble.
    //
    // It used to. Alex, at review: "once the user has started the action it
    // should try to do the whole roll and not abort the animation like it does
    // now" — and separately, that he could not evaluate the roll's sound because
    // he could not hold a roll long enough to hear it. Same cause. A verb that
    // cancels on the exact frame you let go cannot feel like throwing your body
    // at something, which is the entire brief for it (spec §5.4).
    if (this.#rolling && !cmd.roll && this.#rollElapsed >= this.#tuning.rollCommitTime) {
      this.#endRoll();
    } else if (!this.#rolling && canEnterRoll) {
      this.#rolling = true;
      this.#rollElapsed = 0;
      this.#rollBufferRemaining = 0;
    }
    if (this.#rolling) this.#rollElapsed += dt;
    this.#rollCooldownRemaining = Math.max(0, this.#rollCooldownRemaining - dt);
    if (this.#rollBufferRemaining > 0) {
      this.#rollBufferRemaining = Math.max(0, this.#rollBufferRemaining - dt);
    }
    this.#previousRollHeld = cmd.roll;
    this.#updateCrouchCollider(cmd.crouch);
    this.#scrambleCooldownRemaining = Math.max(0, this.#scrambleCooldownRemaining - dt);

    // Tire while rolling, recover while not. Recovery is faster than fatigue by
    // design, so chaining rolls stays viable and this never becomes a resource
    // the player has to manage.
    if (this.#rolling) {
      this.#rollHeldTime += dt;
    } else {
      const recoverRate = (this.#tuning.rollFatigueDelay + this.#tuning.rollFatigueRamp)
        / Math.max(Number.EPSILON, this.#tuning.rollRecoverTime);
      this.#rollHeldTime = Math.max(0, this.#rollHeldTime - recoverRate * dt);
    }

    const currentFacing = this.#state.facing;
    const targetFacing = input.length > 0 ? Math.atan2(input.x, input.z) : cmd.face ?? currentFacing;
    const turnLimit = (this.#rolling ? this.#tuning.turnRateRoll
      : currentSpeed > this.#tuning.walkSpeed ? this.#tuning.turnRateTrot : this.#tuning.turnRateWalk) * dt;
    const facingDelta = clamp(signedAngle(targetFacing - currentFacing), -turnLimit, turnLimit);
    const facing = wrapAngle(currentFacing + facingDelta);
    const turnRate = facingDelta / dt;

    this.#updateHorizontalVelocity(dt, input, facing, wasGrounded, cmd.crouch);
    if (!wasGrounded) {
      this.#velocityY = Math.max(this.#velocityY - this.#tuning.gravity * dt, -this.#tuning.maxFallSpeed);
    } else {
      // Grounded movement is asked for as PURELY HORIZONTAL, and the seating
      // happens after the solve. See GROUND_STICK_SPEED and `#seatOnGround()`:
      // a downward component here is read by Rapier as a direction of travel and
      // suppresses autostep, which is what cost the game every step-up it had.
      //
      // The hover this used to fix is still real — Rapier reports
      // `computedGrounded()` while she is up to `snapToGroundDistance` above the
      // surface, so zeroing the request alone leaves her latched as much as
      // 50 mm off the floor, grounded on every frame and visibly not touching.
      // `#seatOnGround()` is what closes that, and `movement.spec.ts` asserts
      // both halves: she stands within 10 mm of the floor AND she can walk up
      // the everyday step.
      this.#velocityY = 0;
    }

    const requested = { x: this.#velocityX * dt, z: this.#velocityZ * dt };
    this.#controller.computeColliderMovement(this.#collider, {
      x: this.#velocityX * dt,
      y: this.#velocityY * dt,
      z: this.#velocityZ * dt,
    });
    let moved = this.#controller.computedMovement();
    // The first horizontal solve tells the two cases apart. A direct step press
    // is stopped or pushed along its face; walking beside a tunnel wall still
    // progresses parallel to it. Only the latter gets the gentle seating solve.
    if (wasGrounded && this.#crouched && !this.#pressingIntoWall(requested, moved)) {
      this.#controller.computeColliderMovement(this.#collider, {
        x: requested.x, y: -GROUND_STICK_SPEED * dt, z: requested.z,
      });
      moved = this.#controller.computedMovement();
    }
    const center = this.#body.translation();
    const nextCenter = { x: center.x + moved.x, y: center.y + moved.y, z: center.z + moved.z };
    this.#body.setNextKinematicTranslation(nextCenter);

    this.#grounded = this.#controller.computedGrounded();
    if (this.#grounded) {
      this.#groundNormal = this.#readGroundNormal();
      // **Never seat her while she is climbing.** A step is not cleared in one
      // solver call: pressed against the 0.08 m rung the controller returns
      // about 12 mm of rise and a shove BACKWARDS — `moved.x` comes back with
      // the opposite sign to the request — then four or five contact-free frames
      // while she drifts away and leans in again. She gets over the step by
      // accumulating those rises. A settle of 4 mm per frame is faster than the
      // tail of that ratchet, so seating unconditionally fought the climb to a
      // standstill and looked exactly like step-up being switched off.
      //
      // The solver's `moved.y > 0` is not a reliable second signal: under the
      // crawl roof it reports a tiny rise while she walks beside its walls, and
      // then preserves the hover that puts her tail through the roof. A directed
      // wall press is the condition that matters: it stays true through every
      // partial step rise, and is false for the tunnel's parallel side contact.
      if (moved.y <= 0 && (this.#crouched ? !this.#pressingIntoWall(requested, moved) : !this.#touchingWall())) {
        nextCenter.y -= this.#seatOnGround(nextCenter, dt);
        this.#body.setNextKinematicTranslation(nextCenter);
      }
      this.#velocityY = 0;
      this.#airborneTime = 0;
    } else {
      this.#groundNormal = UP;
      this.#airborneTime += dt;
    }
    this.#velocityX = moved.x / dt;
    this.#velocityZ = moved.z / dt;
    const speed = Math.hypot(this.#velocityX, this.#velocityZ);
    if (this.#tryStartScramble(dt, input, facing, cmd, speed, nextCenter.x, nextCenter.y, nextCenter.z)) {
      return this.#advanceScramble(dt);
    }
    const slopeGrade = this.#grounded ? downhillGrade(this.#groundNormal, facing) : 0;
    const gait = this.#gaitFor(speed, cmd);
    const landingImpact = !wasGrounded && this.#grounded && preResolutionVelocityY < 0
      ? -preResolutionVelocityY : 0;
    this.#state = this.#makeState(
      nextCenter.x,
      // Live half-height, not the tuning value: the capsule shrinks when she
      // crouches, and reading the standing figure here would report her feet
      // below the floor for as long as she stayed down.
      nextCenter.y - this.#collider.halfHeight() - this.#tuning.radius - CHARACTER_CONTROLLER_OFFSET,
      nextCenter.z,
      facing,
      speed,
      gait,
      this.#grounded,
      this.#airborneTime,
      landingImpact,
      turnRate,
      slopeGrade,
      0,
    );
    return this.state;
  }

  get state(): CharacterState {
    return { ...this.#state };
  }

  /**
   * Her own collider, so callers can EXCLUDE it from their queries. The camera's
   * collision sweep starts inside her, so without this it would report an
   * immediate hit on Rosalie herself and jam the arm at zero length.
   */
  get collider(): ReturnType<PhysicsWorld['world']['createCollider']> {
    return this.#collider;
  }

  reset(spawn: SpawnPoint): void {
    if (this.#disposed) throw new Error('CharacterController has been disposed');
    const centerY = spawn.y + this.#tuning.halfHeight + this.#tuning.radius + CHARACTER_CONTROLLER_OFFSET;
    this.#body.setTranslation({ x: spawn.x, y: centerY, z: spawn.z }, true);
    this.#body.setNextKinematicTranslation({ x: spawn.x, y: centerY, z: spawn.z });
    this.#velocityX = 0;
    this.#velocityY = 0;
    this.#velocityZ = 0;
    this.#grounded = false;
    this.#groundNormal = UP;
    this.#airborneTime = 0;
    this.#coyoteRemaining = 0;
    this.#rollBufferRemaining = 0;
    this.#previousRollHeld = false;
    this.#rolling = false;
    this.#rollHeldTime = 0;
    this.#rollElapsed = 0;
    // Respawning must not carry a cooldown across, or a test that teleports her
    // and immediately rolls gets a refusal it did nothing to earn.
    this.#rollCooldownRemaining = 0;
    this.#scramblePressTime = 0;
    this.#scrambling = false;
    this.#scrambleElapsed = 0;
    this.#scrambleCooldownRemaining = 0;
    this.#state = this.#makeState(spawn.x, spawn.y, spawn.z, spawn.facing ?? 0, 0, 'still', false, 0, 0, 0, 0, 0);
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#world.world.removeCharacterController(this.#controller);
    this.#world.world.removeRigidBody(this.#body);
    this.#disposed = true;
  }

  #updateHorizontalVelocity(
    dt: number,
    input: { x: number; z: number; length: number },
    facing: number,
    grounded: boolean,
    crouch: boolean,
  ): void {
    if (this.#rolling) {
      const forwardX = Math.sin(facing);
      const forwardZ = Math.cos(facing);
      const slopeGrade = grounded ? downhillGrade(this.#groundNormal, facing) : 0;
      const scalarAccel = (input.length > 0 ? this.#tuning.rollAccel : 0)
        + slopeGrade * this.#tuning.slopeRollAccelScale;
      let rollSpeed = Math.hypot(this.#velocityX, this.#velocityZ);
      if (scalarAccel >= 0) {
        this.#velocityX += forwardX * scalarAccel * dt;
        this.#velocityZ += forwardZ * scalarAccel * dt;
      } else if (rollSpeed > 0) {
        // Uphill removes existing momentum but never creates a backwards thrust.
        const uphillSpeed = Math.max(0, rollSpeed + scalarAccel * dt);
        this.#velocityX *= uphillSpeed / rollSpeed;
        this.#velocityZ *= uphillSpeed / rollSpeed;
      }
      rollSpeed = Math.hypot(this.#velocityX, this.#velocityZ);
      // Fatigue. She tires, rather than being cut off by a timer or metered by a
      // gauge: drag builds after `rollFatigueDelay` so on the flat she coasts to
      // a stop and has to rebuild momentum to re-enter. Downhill still wins,
      // which keeps spec §5.4's authored roll runs intact.
      const fatigue = clamp(
        (this.#rollHeldTime - this.#tuning.rollFatigueDelay) / this.#tuning.rollFatigueRamp,
        0,
        1,
      );
      const drag = this.#tuning.rollDrag + fatigue * this.#tuning.rollFatigueDrag;
      const slowed = Math.max(0, rollSpeed - drag * dt);
      if (rollSpeed > 0) {
        this.#velocityX *= slowed / rollSpeed;
        this.#velocityZ *= slowed / rollSpeed;
      }
      const capped = Math.hypot(this.#velocityX, this.#velocityZ);
      if (capped > this.#tuning.rollMaxSpeed) {
        this.#velocityX *= this.#tuning.rollMaxSpeed / capped;
        this.#velocityZ *= this.#tuning.rollMaxSpeed / capped;
      }
      return;
    }

    // There is no separate run key: normal held movement follows one continuous
    // acceleration curve through walk and naturally reaches trot speed.
    const targetSpeed = crouch ? this.#tuning.creepSpeed : this.#tuning.trotSpeed;
    const targetX = input.x * targetSpeed;
    const targetZ = input.z * targetSpeed;
    const deltaX = targetX - this.#velocityX;
    const deltaZ = targetZ - this.#velocityZ;
    const deltaLength = Math.hypot(deltaX, deltaZ);
    const currentSpeed = Math.hypot(this.#velocityX, this.#velocityZ);
    const targetLength = input.length * targetSpeed;
    const rate = grounded ? (targetLength >= currentSpeed ? this.#tuning.accel : this.#tuning.decel)
      : this.#tuning.airAccel;
    const maxDelta = rate * dt;
    if (deltaLength <= maxDelta || deltaLength === 0) {
      this.#velocityX = targetX;
      this.#velocityZ = targetZ;
    } else {
      this.#velocityX += deltaX / deltaLength * maxDelta;
      this.#velocityZ += deltaZ / deltaLength * maxDelta;
    }
  }

  /**
   * Shrink the collider while creeping, and refuse to stand back up under a
   * ceiling.
   *
   * The standing-up half is the part that matters. Restoring the tall capsule
   * unconditionally would let her stand INSIDE a tunnel roof the moment the key
   * is released, which either ejects her through the geometry or wedges her in
   * it. So the release is conditional: if there is no headroom she stays down,
   * which is also what a real animal does.
   */
  #updateCrouchCollider(crouch: boolean): void {
    if (crouch === this.#crouched) return;

    const standingHalf = this.#tuning.halfHeight;
    const crouchedHalf = Math.max(
      0.001,
      this.#tuning.crouchHeight / 2 - this.#tuning.radius,
    );

    if (crouch) {
      this.#setCapsuleHalfHeight(crouchedHalf);
      this.#crouched = true;
      return;
    }

    // Standing up: is the space above her free? Cast from the top of the
    // crouched capsule to the top of the standing one.
    const centre = this.#body.translation();
    const crouchedTop = centre.y + crouchedHalf + this.#tuning.radius;
    const needed = (standingHalf + this.#tuning.radius) * 2 - this.#tuning.crouchHeight;
    const ray = this.#headroomRay;
    ray.origin.x = centre.x;
    ray.origin.y = crouchedTop;
    ray.origin.z = centre.z;
    ray.dir.x = 0;
    ray.dir.y = 1;
    ray.dir.z = 0;
    const blocked = this.#world.world.castRay(
      ray,
      needed + CHARACTER_CONTROLLER_OFFSET,
      true,
      undefined,
      undefined,
      this.#collider,
    );
    if (blocked) return; // Stay down. She will stand when there is room.

    this.#setCapsuleHalfHeight(standingHalf);
    this.#crouched = false;
  }

  /** Resize the capsule, keeping her FEET where they are rather than her centre. */
  #setCapsuleHalfHeight(halfHeight: number): void {
    const current = this.#collider.halfHeight();
    if (Math.abs(current - halfHeight) < 1e-6) return;
    const centre = this.#body.translation();
    // The origin every caller cares about is her ground contact point, so the
    // centre has to move by the same amount the half-height did or she would
    // sink or hop each time she crouches.
    const delta = halfHeight - current;
    this.#collider.setHalfHeight(halfHeight);
    this.#body.setTranslation({ x: centre.x, y: centre.y + delta, z: centre.z }, true);
  }

  /**
   * Is she in contact with something steep enough to be a step or a wall?
   *
   * `0.5` is the same cut `#readGroundNormal()` implies: a contact whose normal
   * is more sideways than up is not floor. Read from the solver's own collision
   * list so it describes the move that just happened rather than a guess about
   * the next one.
   */
  #pressingIntoWall(requested: { x: number; z: number }, moved: { x: number; z: number }): boolean {
    const requestedLength = Math.hypot(requested.x, requested.z);
    const movedLength = Math.hypot(moved.x, moved.z);
    for (let i = 0; i < this.#controller.numComputedCollisions(); i++) {
      const collision = this.#controller.computedCollision(i);
      if (collision === null || Math.abs(collision.normal1.y) >= 0.5) continue;
      // A side wall in the crawl tunnel is a contact, but not a step: her
      // actual travel is along it. Holding seating off for every wall
      // contact made her ride 18 mm high there, which put her tail through the
      // roof. The step ratchet needs the hold only while she presses INTO the
      // face, where this horizontal projection is near one. Absolute value is
      // deliberate: Rapier owns the collision-normal orientation.
      if (requestedLength > 0 && Math.abs((requested.x * collision.normal1.x + requested.z * collision.normal1.z) / requestedLength) > 0.9) return true;
      if (requestedLength > 0 && movedLength < requestedLength * 0.5) return true;
      if (movedLength > 0 && Math.abs((moved.x * collision.normal1.x + moved.z * collision.normal1.z) / movedLength) > 0.7) return true;
    }
    return false;
  }

  #touchingWall(): boolean {
    for (let i = 0; i < this.#controller.numComputedCollisions(); i++) {
      const collision = this.#controller.computedCollision(i);
      if (collision !== null && Math.abs(collision.normal1.y) < 0.5) return true;
    }
    return false;
  }

  /**
   * How far to press her down this step, in metres, measured not assumed.
   *
   * Sweeps her own capsule straight down from where the solver just put her and
   * returns the free gap under it, capped at `GROUND_STICK_SPEED * dt` so the
   * seating stays a settle rather than a snap. Because the distance is measured
   * it can never push her through a floor, and because it happens outside
   * `computeColliderMovement()` it can never be mistaken for a direction of
   * travel — which is the entire point (D-146).
   *
   * A shape cast rather than a ray: a ray samples one point and she rests on an
   * area, so at a ledge edge or a seam between two solids a ray reports the drop
   * beyond her rather than the surface she is standing on.
   */
  #seatOnGround(center: { x: number; y: number; z: number }, dt: number): number {
    const reach = this.#tuning.snapToGroundDistance + CHARACTER_CONTROLLER_OFFSET;
    const hit = this.#world.world.castShape(
      center, UPRIGHT, DOWN, this.#collider.shape, 0, reach, true,
      undefined, undefined, this.#collider,
    );
    if (hit === null) return 0;
    // `time_of_impact` is in metres because DOWN is a unit vector. Leave the
    // skin behind: seating her the full distance parks her collider exactly on
    // the surface, which is the one place Rapier's queries are least stable.
    const gap = hit.time_of_impact - CHARACTER_CONTROLLER_OFFSET;
    if (gap <= 0) return 0;
    return Math.min(gap, GROUND_STICK_SPEED * dt);
  }

  #readGroundNormal(): GroundNormal {
    let ground: GroundNormal | null = null;
    for (let i = 0; i < this.#controller.numComputedCollisions(); i++) {
      const collision = this.#controller.computedCollision(i);
      if (collision !== null && collision.normal1.y > 0
        && (ground === null || collision.normal1.y > ground.y)) {
        ground = { x: collision.normal1.x, y: collision.normal1.y, z: collision.normal1.z };
      }
    }
    return ground ?? UP;
  }

  #gaitFor(speed: number, cmd: MoveCommand): Gait {
    // Reports the ACTUAL rolling state, not the entry condition. Testing the
    // entry speed here instead made her gait flip back to 'trot' mid-roll as
    // she slowed, so the animation would leave the roll while the physics was
    // still using roll drag and roll steering. Gait is also the stealth sound
    // radius (spec §5.1), so a wrong value here is a stealth bug later.
    if (this.#rolling) return 'roll';
    if (cmd.crouch) return 'creep';
    if (speed < 0.05) return 'still';
    if (speed <= this.#tuning.walkSpeed * 1.05) return 'walk';
    return 'trot';
  }

  #tryStartScramble(
    dt: number,
    input: { x: number; z: number; length: number },
    facing: number,
    cmd: MoveCommand,
    speed: number,
    centerX: number,
    centerY: number,
    centerZ: number,
  ): boolean {
    const forwardX = Math.sin(facing);
    const forwardZ = Math.cos(facing);
    const forwardInput = input.x * forwardX + input.z * forwardZ;
    const commandedSpeed = input.length * (cmd.crouch ? this.#tuning.creepSpeed : this.#tuning.trotSpeed);
    const blocked = commandedSpeed > 0 && speed < commandedSpeed * 0.35;
    // No dedicated key. Spec §4's control list is complete and D-017 rules out
    // adding traversal buttons; the trigger is PRESSING INTO the ledge, which is
    // what a creature with no hands would do. The first implementation required
    // the roll key, which both overloads hold-to-roll and invents a control the
    // spec does not have.
    //
    // Sustained rather than instantaneous, so brushing a wall in passing never
    // hauls her up something. She has to mean it for `scramblePressTime`.
    const eligible = this.#grounded && input.length > 0 && forwardInput >= 0.75
      && blocked && this.#scrambleCooldownRemaining <= 0;
    this.#scramblePressTime = eligible ? this.#scramblePressTime + dt : 0;
    if (!eligible || this.#scramblePressTime < this.#tuning.scramblePressTime) return false;

    const feetY = centerY - this.#tuning.halfHeight - this.#tuning.radius - CHARACTER_CONTROLLER_OFFSET;
    const ray = this.#scrambleRay;
    ray.origin.x = centerX + forwardX * this.#tuning.scrambleForwardClearance;
    ray.origin.y = feetY + this.#tuning.scrambleMaxHeight + CHARACTER_CONTROLLER_OFFSET;
    ray.origin.z = centerZ + forwardZ * this.#tuning.scrambleForwardClearance;
    ray.dir.x = 0;
    ray.dir.y = -1;
    ray.dir.z = 0;
    const ledgeHit = this.#world.world.castRay(
      ray,
      this.#tuning.scrambleMaxHeight + CHARACTER_CONTROLLER_OFFSET * 2,
      true,
      undefined,
      undefined,
      this.#collider,
    );
    if (ledgeHit === null) return false;

    const ledgeY = ray.origin.y - ledgeHit.timeOfImpact;
    const ledgeHeight = ledgeY - feetY;
    if (ledgeHeight < this.#tuning.scrambleMinHeight || ledgeHeight > this.#tuning.scrambleMaxHeight) {
      return false;
    }

    // The same vertical line must be clear for Rosalie's whole standing height.
    // Starting just above the surface avoids treating the ledge itself as a ceiling.
    ray.origin.y = ledgeY + CHARACTER_CONTROLLER_OFFSET;
    ray.dir.y = 1;
    const overheadHit = this.#world.world.castRay(
      ray,
      this.#tuning.halfHeight * 2 + this.#tuning.radius * 2,
      true,
      undefined,
      undefined,
      this.#collider,
    );
    if (overheadHit !== null) return false;

    this.#scrambling = true;
    this.#scrambleElapsed = 0;
    this.#scrambleStartX = centerX;
    this.#scrambleStartY = centerY;
    this.#scrambleStartZ = centerZ;
    this.#scrambleTargetX = ray.origin.x;
    this.#scrambleTargetY = ledgeY + this.#tuning.halfHeight + this.#tuning.radius + CHARACTER_CONTROLLER_OFFSET;
    this.#scrambleTargetZ = ray.origin.z;
    this.#scramblePreviousX = centerX;
    this.#scramblePreviousY = centerY;
    this.#scramblePreviousZ = centerZ;
    this.#scrambleFacing = facing;
    // Through `#endRoll`, not by clearing the flag: a roll cut short by a
    // scramble used to skip the cooldown entirely, so hauling up a ledge was a
    // free reset of the verb. Caught on independent review.
    this.#endRoll();
    this.#rollBufferRemaining = 0;
    return true;
  }

  /**
   * Leave a roll through one door, so the cooldown cannot be skipped.
   *
   * There are two ways a roll ends — the player releasing after the commit
   * window, and a scramble taking over — and the second one used to clear the
   * flag directly and bypass the cooldown.
   */
  #endRoll(): void {
    if (!this.#rolling) return;
    this.#rolling = false;
    this.#rollElapsed = 0;
    this.#rollCooldownRemaining = this.#tuning.rollCooldown;
  }

  #advanceScramble(dt: number): CharacterState {
    this.#scrambleElapsed = Math.min(this.#tuning.scrambleDuration, this.#scrambleElapsed + dt);
    const progress = this.#scrambleElapsed / this.#tuning.scrambleDuration;
    // Smoothstep makes the first pull slow, gives the middle its heave, then
    // settles onto the ledge instead of arriving with a linear, mechanical stop.
    const eased = progress * progress * (3 - 2 * progress);
    const nextCenter = this.#scrambleNextCenter;
    nextCenter.x = this.#scrambleStartX + (this.#scrambleTargetX - this.#scrambleStartX) * eased;
    nextCenter.y = this.#scrambleStartY + (this.#scrambleTargetY - this.#scrambleStartY) * eased;
    nextCenter.z = this.#scrambleStartZ + (this.#scrambleTargetZ - this.#scrambleStartZ) * eased;
    this.#body.setNextKinematicTranslation(nextCenter);

    this.#velocityX = (nextCenter.x - this.#scramblePreviousX) / dt;
    this.#velocityY = (nextCenter.y - this.#scramblePreviousY) / dt;
    this.#velocityZ = (nextCenter.z - this.#scramblePreviousZ) / dt;
    this.#scramblePreviousX = nextCenter.x;
    this.#scramblePreviousY = nextCenter.y;
    this.#scramblePreviousZ = nextCenter.z;

    const complete = progress >= 1;
    if (complete) {
      this.#scrambling = false;
      this.#scrambleCooldownRemaining = this.#tuning.scrambleCooldown;
      this.#velocityX = 0;
      this.#velocityY = 0;
      this.#velocityZ = 0;
      this.#grounded = true;
      this.#groundNormal = UP;
      this.#airborneTime = 0;
    } else {
      this.#grounded = false;
      this.#groundNormal = UP;
      this.#airborneTime += dt;
    }

    const speed = complete ? 0 : Math.hypot(this.#velocityX, this.#velocityZ);
    this.#state = this.#makeState(
      nextCenter.x,
      // Live half-height, not the tuning value: the capsule shrinks when she
      // crouches, and reading the standing figure here would report her feet
      // below the floor for as long as she stayed down.
      nextCenter.y - this.#collider.halfHeight() - this.#tuning.radius - CHARACTER_CONTROLLER_OFFSET,
      nextCenter.z,
      this.#scrambleFacing,
      speed,
      complete ? 'still' : 'scramble',
      this.#grounded,
      this.#airborneTime,
      0,
      0,
      0,
      complete ? 0 : progress,
    );
    return this.state;
  }

  #makeState(
    x: number, y: number, z: number, facing: number, speed: number, gait: Gait,
    grounded: boolean, airborneTime: number, landingImpact: number, turnRate: number, slopeGrade: number,
    scramble: number,
  ): CharacterState {
    return {
      x, y, z, facing, speed, velocityY: this.#velocityY, gait, grounded, airborneTime,
      landingImpact, turnRate, slopeGrade, scramble,
    };
  }
}

function normaliseInput(x: number, z: number): { x: number; z: number; length: number } {
  const length = Math.hypot(x, z);
  if (length === 0) return { x: 0, z: 0, length: 0 };
  const scale = Math.min(1, length) / length;
  return { x: x * scale, z: z * scale, length: Math.min(1, length) };
}

function downhillGrade(normal: GroundNormal, facing: number): number {
  if (normal.y <= 0) return 0;
  return (normal.x * Math.sin(facing) + normal.z * Math.cos(facing)) / normal.y;
}

function signedAngle(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

function wrapAngle(angle: number): number {
  return signedAngle(angle);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
